"use client";

import * as Separator from "@radix-ui/react-separator";

import { Comedian, getPublishedComedians } from "@punchup/data/legacy";
import ComedianCheckboxRow from "./ComedianCheckboxRow";
import ComedianCheckboxRowSkeleton from "./ComedianCheckboxRowSkeleton";
import { FOLLOW_LOCATIONS, getFollowedComedians } from "@punchup/data/legacy";
import {
  FormEventHandler,
  Fragment,
  useCallback,
  useEffect,
  useState,
} from "react";
import { SuggestedComediansEvents, trackEvent } from "@punchup/analytics";
import { useRouter, useSearchParams } from "next/navigation";

import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { followComediansByIds } from "@punchup/data/client/user/follows";
import { getCDNImageURLMap } from "@punchup/utils/getCDNImageURLMap";
import { isInternalPath } from "@punchup/utils";
import { parseAndFixUrl } from "@punchup/utils/parseAndFixUrl";
import { getComediansRanks } from "@punchup/data/legacy";
import { returnRankedUnfollowedComedians } from "@punchup/data/legacy";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useExperiment } from "statsig-react";

// the number of comedians to suggest
const SUGGESTED_COMEDIANS_COUNT = 6;

interface ImageMap {
  [entityId: string]: {
    imageId: string;
    imageUrl: string;
  };
}

interface SuggestedComediansFormProps {
  onComplete?: () => void;
}

export default function SuggestedComediansForm({
  onComplete,
}: SuggestedComediansFormProps) {
  const [suggestedComedians, setSuggestedComedians] = useState<Comedian[]>([]);
  const [comedianImagesMap, setComedianImagesMap] = useState<ImageMap>({});
  const [selectedComedianIds, setSelectedComedianIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const { redirectTo } = Object.fromEntries(searchParams);
  const router = useRouter();
  const supabase = createClientComponentClient<Database>();

  const scrollableDesignExperiment = useExperiment(
    "suggested-comedian-form-scrolling-redesign"
  );
  const isScrollableDesign = scrollableDesignExperiment.config.get(
    "enabled",
    false
  );

  const getSuggestedComedians = useCallback(async () => {
    const allComedians = await getPublishedComedians();
    const followedComedians = await getFollowedComedians({ supabase });
    const comedianRankingScores = await getComediansRanks(supabase);

    const sortedUnfollowedComedians = returnRankedUnfollowedComedians({
      allComedians,
      followedComedians,
      comedianRankingScores,
    });

    const suggestedComedians = sortedUnfollowedComedians.slice(
      0,
      SUGGESTED_COMEDIANS_COUNT
    );

    return [...suggestedComedians];
  }, [supabase]);

  const getComedianImageMap = async (comedians: Comedian[]) => {
    const comedianImages = comedians.map((comedian) => ({
      entityId: comedian.id,
      imageId: comedian.about_img ?? "",
    }));
    const comedianImageMap = await getCDNImageURLMap(comedianImages);
    return comedianImageMap;
  };

  const redirectToHome = useCallback(() => {
    // Invalidate the client Router Cache before navigating so a follow-gated
    // destination (redirectTo) re-renders server-side with the live session
    // instead of a stale, pre-auth render that would bounce the user to
    // /checkout. Same cache-bust as OnboardingStepper.goToDestination — this
    // path (e.g. the zero-suggested-comedians auto-redirect) would otherwise
    // skip it. redirectTo is a user-visible query param, so it only navigates
    // when it is a same-app path (no open redirect).
    router.refresh();
    if (!isInternalPath(redirectTo)) {
      router.push("/");
      return;
    }
    // Carry the welcome-banner param so a gated destination's "arrived from
    // the auth flow" check doesn't depend on the Referer header, which
    // privacy-preserving browsers strip.
    router.push(parseAndFixUrl(`${redirectTo}?showWelcomeBanner=1`));
  }, [router, redirectTo]);

  // log event and retrieve suggested comedians on mount
  useEffect(() => {
    trackEvent(SuggestedComediansEvents.LOADED_SUGGESTED_COMEDIANS_PAGE);

    const fetchData = async () => {
      const suggestedComediansData = await getSuggestedComedians();

      // in the case where we have no comedians to suggest, redirect
      if (suggestedComediansData.length === 0) redirectToHome();

      setSuggestedComedians(suggestedComediansData);
      setSelectedComedianIds(suggestedComediansData.map(({ id }) => id));

      const comedianImageMap = await getComedianImageMap(
        suggestedComediansData
      );
      setComedianImagesMap(comedianImageMap);
    };

    fetchData();
  }, [getSuggestedComedians, redirectToHome]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    trackEvent(SuggestedComediansEvents.CLICKED_SUBSCRIBE, {
      SelectedComedianIds: selectedComedianIds,
      SelectedComediansCount: selectedComedianIds.length,
    });

    if (selectedComedianIds.length > 0) {
      error && setError(null);
      setLoading(true);

      try {
        // Analytics tracking is handled server-side in the v2 API
        await followComediansByIds(
          selectedComedianIds,
          FOLLOW_LOCATIONS.SUGGESTED
        );

        toast({
          title: "Successfully subscribed!",
          variant: "success",
        });
      } catch (err) {
        setLoading(false);
        setError("An error occurred. Please try again.");
      }
    }

    if (onComplete) {
      onComplete();
    } else {
      redirectToHome();
    }
  };

  const handleCheckedChange = (comedianId: string) => (checked: boolean) => {
    if (checked) {
      trackEvent(SuggestedComediansEvents.CHECKED_COMEDIAN, {
        comedianId,
      });

      // add comedian ID
      setSelectedComedianIds((prev) => prev.concat(comedianId));
    } else {
      trackEvent(SuggestedComediansEvents.UNCHECKED_COMEDIAN, {
        comedianId,
      });

      // drop comedian ID
      setSelectedComedianIds((prev) =>
        prev.filter((selectedId) => selectedId !== comedianId)
      );
    }
  };

  if (isScrollableDesign) {
    return (
      <form
        onSubmit={handleSubmit}
        className="flex flex-col items-center w-full h-full"
      >
        <div className="max-h-[66dvh] md:max-h-full overflow-y-auto">
          <div className="flex flex-col items-center w-full gap-0.5 md:gap-0 px-6 md:px-8 pt-4">
            <h1 className="mb-1 font-sans text-xl font-bold leading-none text-center normal-case font-regular md:text-2xl text-primary-blue">
              Follow other comedians
            </h1>
            <h2 className="font-light text-[14px] md:text-[16px] text-tertiary-blue">
              Here are our top recommendations!
            </h2>
          </div>
          <ul className="w-full px-3">
            {suggestedComedians.length > 0
              ? suggestedComedians.map((comedian, index) => {
                  return (
                    <Fragment key={comedian.id}>
                      <li className={loading ? "opacity-50" : ""}>
                        <ComedianCheckboxRow
                          comedian={comedian}
                          comedianImageUrl={
                            comedianImagesMap[comedian.id].imageUrl
                          }
                          loading={loading}
                          checked={selectedComedianIds.includes(comedian.id)}
                          onCheckedChange={handleCheckedChange(comedian.id)}
                        />
                      </li>
                      {index !== suggestedComedians.length - 1 && (
                        <Separator.Root className="border-b border-b-gray-100" />
                      )}
                    </Fragment>
                  );
                })
              : Array.from({ length: SUGGESTED_COMEDIANS_COUNT })
                  .map((_, i) => i)
                  .map((i) => (
                    <Fragment key={i}>
                      <li className="opacity-50 animate-pulse">
                        <ComedianCheckboxRowSkeleton />
                      </li>
                      {i !== SUGGESTED_COMEDIANS_COUNT - 1 && (
                        <Separator.Root className="border-b border-b-gray-100" />
                      )}
                    </Fragment>
                  ))}
          </ul>
        </div>
        {error && (
          <p className="w-full px-4 text-center text-red-500">{error}</p>
        )}
        <div className="z-20 flex flex-col w-full gap-2 px-4 py-[14px] shadow-[0_-5px_14px_rgba(0,0,0,0.1)] md:shadow-none md:px-8 md:pt-3 md:pb-8">
          <button
            type="submit"
            aria-disabled={loading}
            disabled={loading}
            className="w-full md:py-3 py-2 text-[16px] font-semibold transition-colors duration-200 ease-in-out rounded-md bg-primary text-neutral-100 disabled:opacity-50"
          >
            {selectedComedianIds.length > 0
              ? `Subscribe to ${selectedComedianIds.length} and continue`
              : "Continue without subscribing"}
          </button>
          <button
            type="button"
            aria-disabled={loading}
            disabled={loading}
            onClick={() => {
              const allSelected =
                selectedComedianIds.length === suggestedComedians.length;

              if (allSelected) {
                trackEvent(SuggestedComediansEvents.CLICKED_UNSELECT_ALL, {
                  previouslySelectedCount: selectedComedianIds.length,
                });
              } else {
                trackEvent(SuggestedComediansEvents.CLICKED_SELECT_ALL, {
                  previouslySelectedCount: selectedComedianIds.length,
                  totalCount: suggestedComedians.length,
                });
              }

              setSelectedComedianIds(
                allSelected ? [] : suggestedComedians.map((c) => c.id)
              );
            }}
            className="w-full py-2 font-medium transition-colors duration-200 ease-in-out bg-white rounded-md text-[16px] text-neutral-500 disabled:opacity-50 hover:bg-neutral-50 active:bg-neutral-100"
          >
            {selectedComedianIds.length === suggestedComedians.length
              ? "Unselect all"
              : "Select all"}
          </button>
        </div>
      </form>
    );
  }

  // otherwise, render the original design
  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col items-center w-full max-w-sm h-full px-4"
    >
      <div className="flex flex-col items-center w-full gap-0.5 md:gap-1.5 px-6 md:px-8 py-4">
        <h1 className="font-sans text-2xl font-bold leading-9 tracking-tight text-center normal-case font-regular md:text-3xl text-primary-blue">
          Follow other comedians
        </h1>
        <h2 className="font-regular text-[15px] md:text-[16px] text-tertiary-blue">
          Here are our top recommendations!
        </h2>
      </div>
      <ul className="w-full my-3 md:my-6 max-h-[400px] overflow-y-auto">
        {suggestedComedians.length > 0
          ? suggestedComedians.map((comedian, index) => {
              return (
                <Fragment key={comedian.id}>
                  <li className={loading ? "opacity-50" : ""}>
                    <ComedianCheckboxRow
                      comedian={comedian}
                      comedianImageUrl={comedianImagesMap[comedian.id].imageUrl}
                      loading={loading}
                      checked={selectedComedianIds.includes(comedian.id)}
                      onCheckedChange={handleCheckedChange(comedian.id)}
                    />
                  </li>
                  {index !== suggestedComedians.length - 1 && (
                    <Separator.Root className="border-b border-b-gray-100" />
                  )}
                </Fragment>
              );
            })
          : Array.from({ length: SUGGESTED_COMEDIANS_COUNT })
              .map((_, i) => i)
              .map((i) => (
                <Fragment key={i}>
                  <li className="opacity-50 animate-pulse">
                    <ComedianCheckboxRowSkeleton />
                  </li>
                  {i !== SUGGESTED_COMEDIANS_COUNT - 1 && (
                    <Separator.Root className="border-b border-b-gray-100" />
                  )}
                </Fragment>
              ))}
      </ul>
      {error && <p className="w-full px-4 text-center text-red-500">{error}</p>}
      <div className="flex flex-col w-full gap-2">
        <button
          type="submit"
          aria-disabled={loading}
          disabled={loading}
          className="w-full py-3 text-lg font-semibold transition-colors duration-200 ease-in-out rounded-md bg-primary text-neutral-100 disabled:opacity-50"
        >
          {selectedComedianIds.length > 0
            ? `Subscribe to ${selectedComedianIds.length} and continue`
            : "Continue without subscribing"}
        </button>

        <button
          type="button"
          aria-disabled={loading}
          disabled={loading}
          onClick={() => {
            const allSelected =
              selectedComedianIds.length === suggestedComedians.length;

            if (allSelected) {
              trackEvent(SuggestedComediansEvents.CLICKED_UNSELECT_ALL, {
                previouslySelectedCount: selectedComedianIds.length,
              });
            } else {
              trackEvent(SuggestedComediansEvents.CLICKED_SELECT_ALL, {
                previouslySelectedCount: selectedComedianIds.length,
                totalCount: suggestedComedians.length,
              });
            }

            setSelectedComedianIds(
              allSelected ? [] : suggestedComedians.map((c) => c.id)
            );
          }}
          className="w-full py-3 font-medium transition-colors duration-200 ease-in-out bg-white rounded-md text-md text-neutral-500 disabled:opacity-50 hover:bg-neutral-50 active:bg-neutral-100"
        >
          {selectedComedianIds.length === suggestedComedians.length
            ? "Unselect All"
            : "Select All"}
        </button>
      </div>
    </form>
  );
}
